import React from "react";

import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Box, Stack } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Modal from "@src/sharedComponents/Modal/Modal";
import Text from "@src/sharedComponents/Typography/Text";

interface Props {
  closeModal: () => void;
  isMutating: boolean;
  onConfirm: () => void;
  isOpen: boolean;
}

const EnableTaxLossHarvestingModalV2 = (props: Props): JSX.Element => {
  const {
    color: { $surface, $border, $icon },
  } = useTheme();
  return (
    <Modal
      isOpen={props.isOpen}
      closeModal={props.closeModal}
      modalStyle={{ width: "520px" }}
    >
      <Text variant="title2" style={{ paddingBottom: 8 }}>
        Enable tax-loss harvesting?
      </Text>

      <Text color="textSubtle" style={{ paddingBottom: 16 }}>
        Beginning with the next daily trading cycle, Farther will evaluate any
        taxable accounts in this trading group for potential losses to be
        harvested. Trades will be executed as necessary.
      </Text>

      <Accordion disableGutters elevation={0} square>
        <AccordionSummary
          expandIcon={
            <ArrowForwardIosSharpIcon sx={{ color: $icon, width: 14 }} />
          }
        >
          <Text variant="bodyBold">
            Prevent wash sales with your spouse’s accounts
          </Text>
        </AccordionSummary>
        <AccordionDetails>
          <Text color="textSubtle">
            If you would like to link your Farther accounts to your spouse's
            Farther accounts for wash sale prevention purposes, please reach out
            to your advisor.
          </Text>
        </AccordionDetails>
      </Accordion>

      <Stack
        style={{
          border: `1px solid ${$border}`,
          backgroundColor: $surface,
          borderRadius: 5,
          padding: 16,
          marginBottom: 32,
        }}
      >
        <Text variant="bodyBold" style={{ paddingBottom: 8 }}>
          Farther’s investment overlays
        </Text>
        <Text variant="body" color="textSubtle" style={{ paddingBottom: 8 }}>
          By enabling tax-loss harvesting, you are opting in to Farther’s
          investment overlays. Please reach out to client services with any
          questions about terms, conditions and fees associated with enabling
          overlays.
        </Text>
      </Stack>

      <Stack direction={"row"} spacing={2}>
        <Button
          sx={{ flex: 1 }}
          style={{ width: "100%" }}
          variant={"outlined"}
          buttonType={"secondary"}
          text={"Cancel"}
          onClick={props.closeModal}
          disabled={props.isMutating}
        />
        <Button
          sx={{ flex: 1 }}
          style={{ width: "100%" }}
          variant={"contained"}
          buttonType={"primary"}
          text={"Confirm"}
          onClick={props.onConfirm}
          disabled={props.isMutating}
        />
      </Stack>

      {props.isMutating && (
        <div>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LogoLoadingStill onTop />
          </Box>
        </div>
      )}
    </Modal>
  );
};

export default EnableTaxLossHarvestingModalV2;

const AccordionSummary = styled((props: AccordionSummaryProps) => {
  const {
    color: { $icon },
  } = useTheme();

  return (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ color: $icon, width: 14 }} />}
      {...props}
    />
  );
})(() => {
  return {
    border: "none",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
      marginLeft: 8,
    },
    padding: 0,
  };
});

const AccordionDetails = styled(MuiAccordionDetails)(({}) => {
  return {
    padding: "0px 0px 16px 0px",
  };
});

const Accordion = styled(MuiAccordion)(({}) => {
  return {
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  };
});
